import React, { useState } from "react"
import { useTranslation } from "gatsby-plugin-react-i18next"
import Box from "@mui/material/Box"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import TabContext from "@mui/lab/TabContext"
import TabPanel from "@mui/lab/TabPanel"

import PricesTable from "@components/PricesTable"
import PricesChart from "@components/PricesChart"

import iconChart from "../../assets/images/show_chart_24px.svg"
import iconTable from "../../assets/images/table_chart_24px.svg"

import { parsePriceData } from "./helpers"
import { slugMaterialName } from "../../utils/helpers"
import { MobileMenuIcon } from "./MobileMenuIcon"

export default ({
  materialPrices,
  countryCurrency,
  countryCurrencies,
  materialName,
  countryName,
  locale,
  localStoragePrefix,
  defaultButtonIndex,
}) => {
  const { t } = useTranslation()
  const [selectedTab, setSelectedTab] = useState("1")
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null)
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl)

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue)

    // When switching to the Table tab (Tab 2), the currency and range buttons have the first one selected by default.
    // When switching back to the Graph tab (Tab 1), the rangeGroup is set to default range.
    if (newValue === "2") {
      // Change to tab 2, set index to 0
      localStorage.setItem(`${localStoragePrefix}_rangeGroup`, 0)
      localStorage.setItem(`${localStoragePrefix}_currencyGroup`, 0)
    } else {
      // Change back to tab 1, reset index group to default
      localStorage.setItem(
        `${localStoragePrefix}_rangeGroup`,
        defaultButtonIndex
      )
      localStorage.setItem(`${localStoragePrefix}_currencyGroup`, 0)
    }
  }

  const handleOpenMobileMenu = event => {
    setMobileMenuAnchorEl(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null)
  }

  const calculatedPrices = parsePriceData(
    materialPrices,
    countryCurrencies,
    locale
  )

  const localisedMaterialName = t(
    slugMaterialName(materialName) + "_material_name"
  )

  const baseTitle =
    countryName !== "International"
      ? t("price_detail_module_title")
      : t("price_detail_module_title_international")

  const title = baseTitle
    .replace("[%1]", localisedMaterialName)
    .replace("[%2]", t(countryName))

  return (
    <div className="bg-lblue pt-10 pb-5 md:pb-10 desk:p-15 font-inter">
      <div className="w-full md:w-[680px] desk:w-[1136px] m-auto bg-white">
        <p
          className="desk:text-left text-dblue text-20 desk:text-40 
           font-medium mb-4 desk:mb-10 m-6 
           leading-6 desk:leading-10
           desk:ml-[100px] md:pt-[60px] pt-[40px]"
        >
          {title}
        </p>

        <div className="w-full md:w-[680px] desk:w-[936px] m-auto bg-white">
          <Box
            sx={{
              width: "100%",
              typography: "body1",
              marginTop: "2vh",
            }}
          >
            <TabContext value={selectedTab}>
              <Box>
                <Tabs
                  value={selectedTab}
                  onChange={handleChange}
                  aria-label="Visualizacion tabs"
                  sx={{
                    "& .MuiTab-root": {
                      color: "#5B5866",
                      borderBottom: 1,
                      borderColor: "divider",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: "#151124",
                    },
                    "& .MuiTabs-indicator": {
                      height: "4px",
                      backgroundColor: "#52F597",
                    },
                    "& .MuiTabPanel-root": {
                      paddingLeft: "0px !important",
                      paddingRight: "0px !important",
                    },
                  }}
                >
                  <Tab
                    label={
                      <div className={`flex flex-row items-center`}>
                        <img
                          src={iconChart}
                          className={``}
                          alt={t("alt_chart")}
                        />
                        <span className={`text-base ml-2 font-semibold`}>
                          {t("MetalPriceDetail_GRAPH")}
                        </span>
                      </div>
                    }
                    value="1"
                  />
                  <Tab
                    label={
                      <div className={`flex flex-row items-center`}>
                        <img
                          src={iconTable}
                          className={``}
                          alt={t("alt_table")}
                        />
                        <span className={`text-base ml-2 font-semibold`}>
                          {t("MetalPriceDetail_TABLE")}
                        </span>
                      </div>
                    }
                    value="2"
                  />
                  <MobileMenuIcon
                    mobileMenuAnchorEl={mobileMenuAnchorEl}
                    handleOpenMobileMenu={handleOpenMobileMenu}
                  />
                </Tabs>
              </Box>
              <TabPanel
                value="1"
                sx={{
                  paddingLeft: "0px !important",
                  paddingRight: "0px !important",
                }}
              >
                <PricesChart
                  countryCurrency={countryCurrency}
                  locale={locale}
                  calculatedPrices={calculatedPrices}
                  mobileMenuAnchorEl={mobileMenuAnchorEl}
                  mobileMenuOpen={mobileMenuOpen}
                  onMobileMenuClose={handleMobileMenuClose}
                  localStoragePrefix={localStoragePrefix}
                  defaultButtonIndex={defaultButtonIndex}
                />
              </TabPanel>
              <TabPanel
                value="2"
                sx={{
                  paddingLeft: "0px !important",
                  paddingRight: "0px !important",
                }}
              >
                <PricesTable
                  locale={locale}
                  calculatedPrices={calculatedPrices}
                  mobileMenuAnchorEl={mobileMenuAnchorEl}
                  mobileMenuOpen={mobileMenuOpen}
                  onMobileMenuClose={handleMobileMenuClose}
                  localStoragePrefix={localStoragePrefix}
                />
              </TabPanel>
            </TabContext>
          </Box>
        </div>
      </div>
    </div>
  )
}
